import React, { useState, useEffect } from 'react';
import { Calendar, TrendingUp, Package, DollarSign, BarChart3, RefreshCw } from 'lucide-react';
import host from '../../assets/js/Host';
const OrdersReportDashboard = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateFrom, setDateFrom] = useState('2025-06-01');
  const [dateTo, setDateTo] = useState('2025-06-30');

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${host}dashbord/orders-reports/?date_from=${dateFrom}&date_to=${dateTo}`);
      const result = await response.json();
      
      if (result.success) {
        setReportData(result.data);
      } else {
        setError('فشل في جلب البيانات');
      }
    } catch (err) {
      setError('خطأ في الاتصال بالخادم');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const getStatusColor = (statusName) => {
    const colors = {
      'انتظار': '#F59E0B',
      'ملغي': '#EF4444',
      'راجع': '#F97316',
      'لايرد': '#6B7280',
      'طلب جديد': '#3B82F6',
      'طلب غير موجود': '#8B5CF6',
      'قيد التوصيل': '#10B981',
      'طلب مؤجل': '#F59E0B',
      'استبدال': '#6366F1'
    };
    return colors[statusName] || '#6B7280';
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(Math.round(num));
  };

  // حساب إجمالي القيمة والربح بدون المُلغاة
  const calculateTotalsExcludingCancelled = () => {
    if (!reportData) return { totalValue: 0, totalProfit: 0 };
    
    return reportData.report_data
      .filter(item => item.status_name !== 'ملغي')
      .reduce((acc, item) => ({
        totalValue: acc.totalValue + item.total_value,
        totalProfit: acc.totalProfit + item.total_profit
      }), { totalValue: 0, totalProfit: 0 });
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #EBF4FF 0%, #FFFFFF 50%, #EEF2FF 100%)',
      padding: '16px',
      fontFamily: 'Arial, sans-serif',
      direction: 'rtl'
    },
    header: {
      maxWidth: '1280px',
      margin: '0 auto 32px auto',
    },
    headerCard: {
      backgroundColor: '#FFFFFF',
      borderRadius: '16px',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      padding: '24px',
      border: '1px solid #F3F4F6'
    },
    headerTop: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '24px',
      flexWrap: 'wrap'
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    iconContainer: {
      background: 'linear-gradient(135deg, #2563EB, #4F46E5)',
      padding: '12px',
      borderRadius: '12px'
    },
    title: {
      fontSize: '28px',
      fontWeight: 'bold',
      color: '#1F2937',
      margin: 0
    },
    subtitle: {
      color: '#6B7280',
      margin: 0
    },
    refreshButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      background: 'linear-gradient(135deg, #2563EB, #4F46E5)',
      color: '#FFFFFF',
      padding: '12px 24px',
      borderRadius: '12px',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    dateContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      flexWrap: 'wrap'
    },
    dateGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    dateInput: {
      border: '1px solid #D1D5DB',
      borderRadius: '8px',
      padding: '8px 12px',
      fontSize: '14px'
    },
    content: {
      maxWidth: '1280px',
      margin: '0 auto'
    },
    summaryGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
      gap: '20px',
      marginBottom: '32px'
    },
    summaryCard: {
      borderRadius: '16px',
      padding: '24px',
      color: '#FFFFFF'
    },
    summaryContent: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    summaryText: {
      fontSize: '11px',
      fontWeight: '500',
      opacity: 0.8,
      margin: 0,
      lineHeight: '1.3'
    },
    summaryNumber: {
      fontSize: '24px',
      fontWeight: 'bold',
      margin: 0
    },
    statusGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '24px',
      marginBottom: '32px'
    },
    statusCard: {
      backgroundColor: '#FFFFFF',
      borderRadius: '16px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      border: '1px solid #F3F4F6',
      overflow: 'hidden',
      transition: 'box-shadow 0.3s'
    },
    statusHeader: {
      height: '8px'
    },
    statusContent: {
      padding: '24px'
    },
    statusTop: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '16px'
    },
    statusTitle: {
      fontSize: '20px',
      fontWeight: 'bold',
      color: '#1F2937',
      margin: 0
    },
    statusDot: {
      width: '16px',
      height: '16px',
      borderRadius: '50%'
    },
    statusDetails: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px'
    },
    statusRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    statusLabel: {
      color: '#6B7280'
    },
    statusValue: {
      fontWeight: 'bold',
      fontSize: '16px'
    },
    progressContainer: {
      marginTop: '16px'
    },
    progressHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '12px',
      color: '#6B7280',
      marginBottom: '4px'
    },
    progressBar: {
      width: '100%',
      backgroundColor: '#E5E7EB',
      borderRadius: '9999px',
      height: '8px'
    },
    progressFill: {
      height: '8px',
      borderRadius: '9999px'
    },
    loading: {
      maxWidth: '1280px',
      margin: '0 auto',
      textAlign: 'center',
      padding: '48px 0'
    },
    loadingContent: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px'
    },
    error: {
      maxWidth: '1280px',
      margin: '0 auto'
    },
    errorCard: {
      backgroundColor: '#FEF2F2',
      border: '1px solid #FECACA',
      borderRadius: '12px',
      padding: '24px',
      textAlign: 'center'
    },
    errorText: {
      color: '#DC2626',
      fontSize: '16px'
    },
    dateRange: {
      backgroundColor: '#FFFFFF',
      borderRadius: '16px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      padding: '24px',
      border: '1px solid #F3F4F6',
      textAlign: 'center'
    },
    dateRangeTitle: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: '#1F2937',
      marginBottom: '8px'
    },
    dateRangeText: {
      color: '#6B7280'
    },
    highlight: {
      fontWeight: 'bold',
      color: '#2563EB'
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerCard}>
          <div style={styles.headerTop}>
            <div style={styles.headerLeft}>
              <div style={styles.iconContainer}>
                <BarChart3 size={32} color="#FFFFFF" />
              </div>
              <div>
                <h1 style={styles.title}>تقارير الطلبات</h1>
                <p style={styles.subtitle}>تحليل شامل لأداء الطلبات</p>
              </div>
            </div>
            <button
              onClick={fetchReport}
              disabled={loading}
              style={{
                ...styles.refreshButton,
                opacity: loading ? 0.5 : 1,
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
              onMouseOver={(e) => {
                if (!loading) e.target.style.background = 'linear-gradient(135deg, #1D4ED8, #3730A3)';
              }}
              onMouseOut={(e) => {
                if (!loading) e.target.style.background = 'linear-gradient(135deg, #2563EB, #4F46E5)';
              }}
            >
              <RefreshCw 
                size={20} 
                style={{ 
                  animation: loading ? 'spin 1s linear infinite' : 'none',
                  transformOrigin: 'center'
                }} 
              />
              <span>تحديث</span>
            </button>
          </div>

          {/* Date Range Selector */}
          <div style={styles.dateContainer}>
            <div style={styles.dateGroup}>
              <Calendar size={20} color="#6B7280" />
              <label style={{ color: '#374151', fontWeight: '500' }}>من:</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={styles.dateInput}
              />
            </div>
            <div style={styles.dateGroup}>
              <label style={{ color: '#374151', fontWeight: '500' }}>إلى:</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={styles.dateInput}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div style={styles.loading}>
          <div style={styles.loadingContent}>
            <RefreshCw 
              size={24} 
              color="#2563EB"
              style={{ 
                animation: 'spin 1s linear infinite',
                transformOrigin: 'center'
              }}
            />
            <span style={{ fontSize: '18px', color: '#6B7280' }}>جاري تحميل البيانات...</span>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div style={styles.error}>
          <div style={styles.errorCard}>
            <p style={styles.errorText}>{error}</p>
          </div>
        </div>
      )}

      {/* Report Data */}
      {reportData && !loading && (
        <div style={styles.content}>
          {/* Summary Cards */}
          <div style={styles.summaryGrid}>
            {/* إجمالي الطلبات بدون الإلغاء */}
            <div style={{
              ...styles.summaryCard,
              background: 'linear-gradient(135deg, #2563EB, #1D4ED8)'
            }}>
              <div style={styles.summaryContent}>
                <div>
                  <p style={styles.summaryText}>إجمالي الطلبات (بدون الإلغاء)</p>
                  <p style={styles.summaryNumber}>
                    {formatNumber(reportData.summary.total_orders_excluding_cancelled)}
                  </p>
                </div>
                <Package size={40} style={{ opacity: 0.7 }} />
              </div>
            </div>

            {/* الطلبات الناجحة (قيد التوصيل) */}
            <div style={{
              ...styles.summaryCard,
              background: 'linear-gradient(135deg, #059669, #047857)'
            }}>
              <div style={styles.summaryContent}>
                <div>
                  <p style={styles.summaryText}>الطلبات الناجحة (قيد التوصيل)</p>
                  <p style={styles.summaryNumber}>
                    {formatNumber(reportData.report_data.find(item => item.status_name === 'قيد التوصيل')?.orders_count || 0)}
                  </p>
                </div>
                <TrendingUp size={40} style={{ opacity: 0.7 }} />
              </div>
            </div>

            {/* إجمالي القيم من الطلبات قيد التوصيل */}
            <div style={{
              ...styles.summaryCard,
              background: 'linear-gradient(135deg, #7C3AED, #5B21B6)'
            }}>
              <div style={styles.summaryContent}>
                <div>
                  <p style={styles.summaryText}>إجمالي القيم (قيد التوصيل)</p>
                  <p style={styles.summaryNumber}>
                    {formatNumber(reportData.report_data.find(item => item.status_name === 'قيد التوصيل')?.total_value || 0)}
                  </p>
                </div>
                <DollarSign size={40} style={{ opacity: 0.7 }} />
              </div>
            </div>

            {/* إجمالي الأرباح من الطلبات قيد التوصيل */}
            <div style={{
              ...styles.summaryCard,
              background: 'linear-gradient(135deg, #10B981, #059669)'
            }}>
              <div style={styles.summaryContent}>
                <div>
                  <p style={styles.summaryText}>إجمالي الأرباح (قيد التوصيل)</p>
                  <p style={styles.summaryNumber}>
                    {formatNumber(reportData.report_data.find(item => item.status_name === 'قيد التوصيل')?.total_profit || 0)}
                  </p>
                </div>
                <TrendingUp size={40} style={{ opacity: 0.7 }} />
              </div>
            </div>

            {/* معدل الإلغاء */}
            <div style={{
              ...styles.summaryCard,
              background: 'linear-gradient(135deg, #EF4444, #DC2626)'
            }}>
              <div style={styles.summaryContent}>
                <div>
                  <p style={styles.summaryText}>معدل الإلغاء</p>
                  <p style={styles.summaryNumber}>
                    {(() => {
                      const cancelledOrders = reportData.report_data.find(item => item.status_name === 'ملغي')?.orders_count || 0;
                      const totalOrders = reportData.report_data.reduce((sum, item) => sum + item.orders_count, 0);
                      return totalOrders > 0 ? ((cancelledOrders / totalOrders) * 100).toFixed(1) : '0';
                    })()}%
                  </p>
                </div>
                <Package size={40} style={{ opacity: 0.7, transform: 'rotate(180deg)' }} />
              </div>
            </div>

            {/* معدل الإرجاع بدون الطلبات الملغية */}
            <div style={{
              ...styles.summaryCard,
              background: 'linear-gradient(135deg, #F97316, #EA580C)'
            }}>
              <div style={styles.summaryContent}>
                <div>
                  <p style={styles.summaryText}>معدل الإرجاع (بدون الملغية)</p>
                  <p style={styles.summaryNumber}>
                    {(() => {
                      const returnedOrders = reportData.report_data.find(item => item.status_name === 'راجع')?.orders_count || 0;
                      const totalOrdersExcludingCancelled = reportData.summary.total_orders_excluding_cancelled;
                      return totalOrdersExcludingCancelled > 0 ? ((returnedOrders / totalOrdersExcludingCancelled) * 100).toFixed(1) : '0';
                    })()}%
                  </p>
                </div>
                <RefreshCw size={40} style={{ opacity: 0.7 }} />
              </div>
            </div>
          </div>

          {/* Status Cards */}
          <div style={styles.statusGrid}>
            {(() => {
              // ترتيب الحالات حسب التسلسل المطلوب
              const statusOrder = ['انتظار', 'قيد التوصيل', 'طلب مؤجل', 'استبدال', 'راجع', 'لايرد', 'ملغي'];
              
              // ترتيب البيانات حسب التسلسل المحدد
              const sortedStatuses = statusOrder.map(statusName => 
                reportData.report_data.find(item => item.status_name === statusName)
              ).filter(status => status && status.orders_count > 0);
              
              // إضافة أي حالات أخرى غير مدرجة في التسلسل
              const remainingStatuses = reportData.report_data.filter(status => 
                status.orders_count > 0 && !statusOrder.includes(status.status_name)
              );
              
              return [...sortedStatuses, ...remainingStatuses].map((status, index) => (
                <div 
                  key={index} 
                  style={styles.statusCard}
                  onMouseOver={(e) => {
                    e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                  }}
                >
                  <div style={{
                    ...styles.statusHeader,
                    backgroundColor: getStatusColor(status.status_name)
                  }}></div>
                  
                  <div style={styles.statusContent}>
                    <div style={styles.statusTop}>
                      <h3 style={styles.statusTitle}>{status.status_name}</h3>
                      <div style={{
                        ...styles.statusDot,
                        backgroundColor: getStatusColor(status.status_name)
                      }}></div>
                    </div>

                    <div style={styles.statusDetails}>
                      <div style={styles.statusRow}>
                        <span style={styles.statusLabel}>عدد الطلبات:</span>
                        <span style={{
                          ...styles.statusValue,
                          color: '#1F2937'
                        }}>{formatNumber(status.orders_count)}</span>
                      </div>

                      <div style={styles.statusRow}>
                        <span style={styles.statusLabel}>النسبة العامة:</span>
                        <span style={{
                          ...styles.statusValue,
                          color: '#2563EB'
                        }}>{status.percentage}%</span>
                      </div>

                      <div style={{
                        ...styles.statusRow,
                        background: status.status_name === 'ملغي' 
                          ? 'linear-gradient(135deg, #F3F4F6, #E5E7EB)' 
                          : 'linear-gradient(135deg, #ECFDF5, #D1FAE5)',
                        padding: '12px 16px',
                        borderRadius: '12px',
                        border: status.status_name === 'ملغي' 
                          ? '2px solid #E5E7EB' 
                          : '2px solid #10B981',
                        boxShadow: status.status_name === 'ملغي' 
                          ? 'none' 
                          : '0 4px 12px rgba(16, 185, 129, 0.15)'
                      }}>
                        <span style={{
                          ...styles.statusLabel,
                          fontWeight: 'bold',
                          color: status.status_name === 'ملغي' ? '#6B7280' : '#047857'
                        }}>
                          🎯 النسبة (بدون المُلغاة):
                        </span>
                        <span style={{
                          ...styles.statusValue,
                          fontSize: '18px',
                          fontWeight: 'bold',
                          color: status.status_name === 'ملغي' ? '#6B7280' : '#047857',
                          textShadow: status.status_name === 'ملغي' ? 'none' : '0 1px 2px rgba(4, 120, 87, 0.1)'
                        }}>
                          {status.status_name === 'ملغي' ? '0' : status.percentage_excluding_cancelled}%
                        </span>
                      </div>

                      <div style={styles.statusRow}>
                        <span style={styles.statusLabel}>إجمالي القيمة:</span>
                        <span style={{
                          ...styles.statusValue,
                          color: '#1F2937'
                        }}>{formatNumber(status.total_value)}</span>
                      </div>

                      <div style={styles.statusRow}>
                        <span style={styles.statusLabel}>إجمالي الربح:</span>
                        <span style={{
                          ...styles.statusValue,
                          color: status.total_profit >= 0 ? '#059669' : '#DC2626'
                        }}>
                          {formatNumber(status.total_profit)}
                        </span>
                      </div>

                      {/* Progress Bar */}
                      <div style={styles.progressContainer}>
                        <div style={styles.progressHeader}>
                          <span>النسبة من الإجمالي</span>
                          <span>{status.status_name === 'ملغي' ? '0' : status.percentage_excluding_cancelled}%</span>
                        </div>
                        <div style={styles.progressBar}>
                          <div 
                            style={{
                              ...styles.progressFill,
                              backgroundColor: getStatusColor(status.status_name),
                              width: `${status.status_name === 'ملغي' ? 0 : Math.min(status.percentage_excluding_cancelled, 100)}%`
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ));
            })()}
          </div>

          {/* Date Range Info */}
          <div style={styles.dateRange}>
            <h3 style={styles.dateRangeTitle}>فترة التقرير</h3>
            <p style={styles.dateRangeText}>
              من <span style={styles.highlight}>{reportData.date_from}</span> 
              {' '}إلى <span style={styles.highlight}>{reportData.date_to}</span>
            </p>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default OrdersReportDashboard;